<template>
  <BLayout :colors="$site.colors" :header-title="$t('pages.book.complete.title')" :header-back-route="homeRoute">
    <BContainer class="mt-12">
      <div class="max-w-xl mx-auto">
        <BErrors v-if="error" :errors="[error]" class="mb-4" />

        <div v-if="reservation">
          <BCard :title="$t('pages.book.complete.bookingConfirmed')" class="mb-4 ">
            <div class="mb-6">
              <p class="text-secondary mb-2">
                {{ $t('pages.book.complete.bookingConfirmedMessage') }}
              </p>
              <p class="text-secondary mb-2">
                {{ $t('pages.book.complete.bookingConfirmedEmail') }}
              </p>
            </div>

            <div v-if="forms && forms.length > 0" class="mb-6">
              <p>{{ $t('pages.book.complete.formsSentToComplete') }}</p>

              <div>
                <div v-for="form in forms" :key="form.id" class="flex items-center justify-between">
                  <p class="text-sm text-secondary">
                    {{ form.title }}<br>
                    <small class="font-weight-light">{{ $format.date(form.dateLocal) }}, {{ $format.time(form.dateLocal) }}</small>
                  </p>

                  <UButton v-if="form.requestState == `requested`" @click="submitForm(form)">
                    {{ $t('pages.book.complete.formsFillInNow') }} »
                  </UButton>

                  <UBadge v-if="form.requestState === 'completed'" color="green">
                    {{ $t('pages.book.complete.formsCompleted') }}
                  </UBadge>
                </div>
              </div>
            </div>

            <p class="text-sm text-secondary mb-3">
              {{ $format.date(reservation.startTimeUTC) }}
            </p>

            <div class="rounded bg-secondary mb-6">
              <div v-for="(reservationService, reservationServiceIndex) in reservation.services" :key="`reservationService-`+ reservationServiceIndex" class="flex items-start justify-between p-4 border-b last:border-none">
                <p class="text-sm text-secondary">
                  <span>
                    {{ reservationService.service.title }}
                  </span>
                  <span>
                    @ {{ formatTime(reservationService.startTime) }}
                  </span>
                  <span>
                    {{ $t('general.withEmployee', {employee: reservationService.employee.firstName}) }}

                    <span v-if="reservationService.resources.length > 0" class="mt-1 opacity-80">
                      (<span v-for="(resource, resourceIndex) in reservationService.resources" :key="`service-${reservationServiceIndex}-resource-${resourceIndex}`">
                        {{ resource.serviceResource.displayLabel }}
                        <template v-if="resourceIndex < reservationService.resources.length - 1">,</template>
                      </span>)
                    </span>
                  </span>
                </p>

                <p v-if="$site.settings.bookShowServicePrices && reservationService.price > 0" class="text-sm text-secondary font-semibold">
                  <template v-if="reservationService.service.priceFrom">
                    From
                  </template>
                  <span v-if="reservationService.priceLabel != reservationService.discountedPriceLabel">
                    <del>{{ reservationService.priceLabel }}</del> {{ reservationService.discountedPriceLabel }}
                  </span>
                  <span v-else>
                    {{ reservationService.priceLabel }}
                  </span>
                </p>
              </div>
            </div>

            <div v-if="reservation.customFields && reservation.customFields.length > 0" class="mb-4 text-sm text-secondary">
              <p v-for="customField in reservation.customFields" :key="customField.id">
                <span class="opacity-70">
                  {{ customField.name }}:
                </span>
                <span class="font-semibold">
                  {{ customField.value }}
                </span>
              </p>
            </div>
          </BCard>

          <div id="analyticsConversionCode" class="hidden" />

          <div v-if="payment">
            <BCard v-if="payment.pass" :title="$t('general.paymentPass')" class="mb-6">
              <p class="text-sm text-secondary mb-1">
                {{ payment.pass.pass.name }}
              </p>
              <p v-if="payment.pass.usesRemaining >= 0" class="text-sm text-secondary mb-1">
                {{ $t('general.usesRemaining', payment.pass.usesRemaining, {count: payment.pass.usesRemaining}) }}
              </p>
              <p v-if="payment.pass.usesRemaining == '-1'" class="text-sm text-secondary mb-1">
                {{ $t('general.unlimitedUse') }}
              </p>

              <p v-if="payment.pass.expiryDate > 0" class="text-sm text-secondary mb-1">
                {{ $t('general.expiresDate', {date: $format.date(payment.pass.expiryDate)}) }}
              </p>
              <p v-if="payment.pass.expiryDate == null" class="text-sm text-secondary mb-1">
                {{ $t('general.neverExpires') }}
              </p>
            </BCard>

            <BCard
              v-if="payment.order"
              :title="$t('general.paymentSale')"
              :subtitle="`${ $t('general.sale') }: #${ payment.order.orderNumber }`"
              class="mb-6"
            >
              <template #control>
                <UBadge v-if="payment.order.paid" color="green">
                  {{ $t('general.paid') }}
                </UBadge>
              </template>

              <dl>
                <div v-if="showPaymentSummary" class="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt class="text-sm font-medium text-secondary">
                    {{ $t('general.total') }}
                  </dt>
                  <dd class="mt-1 text-sm text-secondary sm:col-span-2 sm:mt-0">
                    {{ payment.order.totalLabel }}
                  </dd>
                </div>

                <div class="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt class="text-sm font-medium text-secondary">
                    {{ $t('general.payments') }}
                  </dt>
                  <dd class="mt-1 text-sm text-secondary sm:col-span-2 sm:mt-0">
                    {{ payment.order.paymentsLabel }}
                  </dd>
                </div>

                <div v-if="showPaymentSummary" class="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt class="text-sm font-medium text-secondary">
                    {{ $t('general.remainder') }}
                  </dt>
                  <dd class="mt-1 text-sm text-secondary sm:col-span-2 sm:mt-0">
                    {{ payment.order.remainderLabel }}
                  </dd>
                </div>
              </dl>
            </BCard>
          </div>
        </div>
      </div>
    </BContainer>
  </BLayout>
</template>

<script>
import Utils from '@/utils'

definePageMeta({
  tabs: ['online'],
  middleware: ['redirects']
});

export default defineNuxtComponent({
  async asyncData () {
    const route = useRoute()
    const { $api, $t } = useNuxtApp()
    const query = route.query

    const response = await $api.customer.reservation(query.id, query.date, { id: query.customer }, query.signature)

    if (response.success) {
      return {
        reservation: response.reservation,
        payment: response.payment,
        forms: response.forms
      }
    } else {
      return {
        error: $t('pages.book.complete.reservationNotFound')
      }
    }
  },
  data () {
    return {
      reservation: null,
      payment: null,
      error: null
    }
  },
  computed: {
    homeRoute () {
      return this.$store.getters.homeRoute || { path: '/' }
    },
    showPaymentSummary () {
      return this.$site.settings.bookShowServicePrices || this.payment?.order?.paid
    }, 
    isAppLayout () {
      return this.$store.getters.layout === 'app'
    }
  },
  mounted () {
    if (this.isAppLayout && window?.flutter_inappwebview?.callHandler) {
      this.emitFlutterEvent('changed-reservation')
    }

    if (this.$fb && this.$fb.isEnabled) {
      this.$fb.track('Schedule', {}, { eventID: this.reservation.unique_key })
    }

    if (this.$site.settings.analyticsConversionCode) {
      this.$postscribe('#analyticsConversionCode', this.$site.settings.analyticsConversionCode)
    }

    const { gtag } = useGtag()
    
    gtag(
      'event',
      'complete_booking',
      {
        event_category: 'purchase',
        event_label: 'New Booking',
        services: this.reservation?.services?.map(service => service.service.title),
        idCustomer: this.reservation?.customer?.id,
        paid: this.payment?.order?.totalLabel,
        total: this.reservation?.priceLabel
      }
    )

    // this.$analytics.conversion()

    this.$store.dispatch('booking/clear')

    if (this.$site.isEmbed && window.parent) {
      if (this.$route.query.action && this.$route.query.action === 'move') {
        window.postRobot?.send(window.parent, 'bookapp.event.reservation.moved', this.reservation)
      } else {
        window.postRobot?.send(window.parent, 'bookapp.event.reservation.booked', this.reservation)
      }

    }
  },
  methods: {
    formatHtml (text) {
      return Utils.text.formatHtml(text)
    },
    formatTime (time) {
      return Utils.text.formatMinuteTime(time, this.$site.location)
    },
    submitForm (form) {
      if (form.requestState === 'requested') {
        const formRoute = { path: '/customer/form/submit', query: { id: form.id, redirect: this.$route.fullPath } }

        if (this.$auth.loggedIn) {
          this.$router.push(formRoute)
        } else {
          this.$router.push({ path: '/customer/login/code', query: { customer: this.reservation.customer.id, email: this.reservation.customer.email, redirect: this.$router.resolve(formRoute).href } })
        }
      }
    },
    emitFlutterEvent (name) {
      return window?.flutter_inappwebview?.callHandler(`bookapp-${name}`)
    }
  }
})
</script>

<style scoped>
.reservation-details--services--service
{
  font-weight: bold;
  font-size: 16px;
}

.reservation-details--services--employee
{
  color: var(--bookapp-content-text-color) !important;
  font-size: 13px;
}

.reservation-details--services--price
{
  font-size: 14px;
}

.reservation-details--date
{
  font-size: 14px;
  margin-bottom:20px;
}

.reservation-details--services--customfields--name {
  font-weight: bold;
}
</style>

